import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PaintBrushIcon,
  SwatchIcon,
  AdjustmentsHorizontalIcon,
  EyeDropperIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useTheme } from '../../contexts/ThemeContext';
import { H2, H3, Body, Card, Button } from './index';
import { Modal } from './Modal';
import ThemeToggle from './ThemeToggle';

interface ThemeCustomizerProps {
  isOpen: boolean;
  onClose: () => void;
}

const ThemeCustomizer: React.FC<ThemeCustomizerProps> = ({ isOpen, onClose }) => {
  const { theme, mode, setMode, isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState<'colors' | 'typography' | 'spacing'>('colors');
  const [selectedColor, setSelectedColor] = useState<string>('primary');

  const tabs = [
    { id: 'colors', label: 'Colors', icon: SwatchIcon },
    { id: 'typography', label: 'Typography', icon: AdjustmentsHorizontalIcon },
    { id: 'spacing', label: 'Spacing', icon: EyeDropperIcon },
  ];

  const colorCategories = [
    { id: 'primary', label: 'Primary', colors: theme.colors.primary },
    { id: 'secondary', label: 'Secondary', colors: theme.colors.secondary },
    { id: 'accent', label: 'Accent', colors: theme.colors.accent },
    { id: 'neutral', label: 'Neutral', colors: theme.colors.neutral },
  ];

  const renderColorPalette = (colors: Record<string, string>, title: string) => (
    <div className="mb-6">
      <H3 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
        {title}
      </H3>
      <div className="grid grid-cols-10 gap-2">
        {Object.entries(colors).map(([shade, color]) => (
          <motion.div
            key={shade}
            className="group relative cursor-pointer"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <div
              className="w-8 h-8 rounded-md border-2 border-gray-200 dark:border-gray-700 shadow-sm"
              style={{ backgroundColor: color }}
              title={`${title} ${shade}: ${color}`}
            />
            <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                {shade}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );

  const renderTypographySection = () => (
    <div className="space-y-6">
      <div>
        <H3 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
          Font Families
        </H3>
        <div className="space-y-3">
          {Object.entries(theme.typography.fontFamily).map(([name, family]) => (
            <div key={name} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <span className="text-sm font-medium capitalize">{name}</span>
              <span className="text-sm text-gray-600 dark:text-gray-400 font-mono">
                {family}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <H3 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
          Font Sizes
        </H3>
        <div className="space-y-3">
          {Object.entries(theme.typography.fontSize).map(([size, { size: fontSize, lineHeight }]) => (
            <div key={size} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium">{size}</span>
                <span 
                  className="text-gray-800 dark:text-gray-200"
                  style={{ 
                    fontSize,
                    lineHeight,
                    fontFamily: theme.typography.fontFamily.sans
                  }}
                >
                  Sample Text
                </span>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                {fontSize} / {lineHeight}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <H3 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
          Font Weights
        </H3>
        <div className="space-y-3">
          {Object.entries(theme.typography.fontWeight).map(([weight, value]) => (
            <div key={weight} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium capitalize">{weight}</span>
                <span 
                  className="text-gray-800 dark:text-gray-200"
                  style={{ 
                    fontWeight: value,
                    fontFamily: theme.typography.fontFamily.sans
                  }}
                >
                  Sample Text
                </span>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                {value}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderSpacingSection = () => (
    <div className="space-y-6">
      <div>
        <H3 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
          Spacing Scale
        </H3>
        <div className="space-y-3">
          {Object.entries(theme.spacing).map(([size, value]) => (
            <div key={size} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium">{size}</span>
                <div 
                  className="bg-primary-500 h-4 rounded"
                  style={{ width: value }}
                />
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                {value}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <H3 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
          Border Radius
        </H3>
        <div className="space-y-3">
          {Object.entries(theme.borderRadius).map(([size, value]) => (
            <div key={size} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium">{size}</span>
                <div 
                  className="bg-primary-500 w-8 h-8"
                  style={{ borderRadius: value }}
                />
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                {value}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <H3 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
          Shadows
        </H3>
        <div className="space-y-3">
          {Object.entries(theme.shadows).map(([size, value]) => (
            <div key={size} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium">{size}</span>
                <div 
                  className="bg-white dark:bg-gray-700 w-8 h-8 rounded-md"
                  style={{ boxShadow: value }}
                />
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 font-mono truncate max-w-32">
                {value}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'colors':
        return (
          <div className="space-y-6">
            {colorCategories.map(category => 
              renderColorPalette(category.colors, category.label)
            )}
            
            <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
              <H3 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                Semantic Colors
              </H3>
              <div className="grid grid-cols-4 gap-4">
                {Object.entries(theme.colors.semantic).map(([name, color]) => (
                  <div key={name} className="flex items-center space-x-2">
                    <div
                      className="w-6 h-6 rounded-full border border-gray-300 dark:border-gray-600"
                      style={{ backgroundColor: color }}
                    />
                    <span className="text-sm capitalize">{name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      case 'typography':
        return renderTypographySection();
      case 'spacing':
        return renderSpacingSection();
      default:
        return null;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <PaintBrushIcon className="w-6 h-6 text-primary-600" />
            <H2>Theme Customizer</H2>
          </div>
          <div className="flex items-center space-x-3">
            <ThemeToggle variant="dropdown" />
            <Button variant="ghost" size="sm" onClick={onClose}>
              <XMarkIcon className="w-5 h-5" />
            </Button>
          </div>
        </div>

        <div className="flex space-x-6">
          {/* Sidebar */}
          <div className="w-48 flex-shrink-0">
            <nav className="space-y-2">
              {tabs.map(tab => (
                <motion.button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`
                    w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left
                    transition-colors duration-200
                    ${activeTab === tab.id
                      ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                    }
                  `}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <tab.icon className="w-5 h-5" />
                  <span className="text-sm font-medium">{tab.label}</span>
                </motion.button>
              ))}
            </nav>

            <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <H3 className="mb-2 text-sm font-medium">Current Theme</H3>
              <Body className="text-sm text-gray-600 dark:text-gray-400">
                {theme.name}
              </Body>
              <div className="mt-3">
                <ThemeToggle variant="switch" showLabel size="sm" />
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <Card className="p-6 max-h-96 overflow-y-auto">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  {renderTabContent()}
                </motion.div>
              </AnimatePresence>
            </Card>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <Body className="text-sm text-gray-600 dark:text-gray-400">
              Edna Design System v1.0.0
            </Body>
            <div className="flex space-x-2">
              <Button variant="outline" size="sm">
                Export Theme
              </Button>
              <Button variant="outline" size="sm">
                Reset to Default
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ThemeCustomizer;
